import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';

import { ValTableState } from '@valamar/val-table';
import { AccountEditInterface } from '../interfaces/vrc/account-edit.interface';
import { AccountInterface } from '../interfaces/vrc/account.interface';
import { AdditionalInterface } from '../interfaces/vrc/account/additional.interface';
import { AddressInterface } from '../interfaces/vrc/account/address.interface';
import { InterestInterface } from '../interfaces/vrc/account/interest.interface';
import { CommunicationRequestInterface } from '../interfaces/vrc/communication-request.interface';
import { CommunicationUpdateAccountRequestInterface } from '../interfaces/vrc/communication-update-account-request.interface';
import { MappingFieldsInterface } from '../interfaces/vrc/mapping-fields.interface';
import { ValUtilsService } from './val-utils.service';
import { VrcDataService } from './vrc-data.service';
import { VrcStorageService } from './vrc-storage.service';
import { ValToastService } from '@valamar/val-toast';

@Injectable()
export class VrcAccountUtilsService {
  private _ms = inject(ValToastService);
  private _srvc = inject(VrcDataService);
  private _st = inject(VrcStorageService);
  private _u = inject(ValUtilsService);

  public initNewAccount = (
    firstName: string | null = null,
    lastName: string | null = null,
    language: string | null = null,
    phone: string | null = null,
    email: string | null = null,
  ): AccountInterface => {
    return {
      id: null,
      isActive: true,
      isDeleted: false,
      createdByUser: null,
      createdDate: null,
      modifiedByUser: null,
      modifiedDate: null,
      firstName: firstName,
      lastName: lastName,
      salutation: null,
      gender: null,
      language: language ? this._st.languages.filter(x => x.name.toLowerCase() === language.toLowerCase())
        .map(x => x.code).reduceRight((_a, b) => b, null) : null,
      birthDate: null,
      age: null,
      oib: null,
      familyStatuses: [],
      address: {
        country: '',
        city: '',
        street: '',
        zipCode: ''
      },
      contact: {
        phone: phone,
        email: email,
        viber: null,
        messenger: null,
        whatsApp: null,
        additional: {
          phones: [],
          emails: [],
          tempEmails: []
        }
      },
      additionalInformation: {
        revenueSegment: '',
        isBlacklisted: false,
        gdprDelete: false,
        isVip: false,
        customerMemberType: '',
        isOtaRestricted: false,
        extraMeal: false,
        babyCot: false,
        travelWithPets: false,
        isTopGuest: false,
        inResponsysProgram: null
      },
      loyalties: [],
      advertisingPermissions: this.FixAdvertisingPermissions(null),
      products: this.FixProducts(null),
      interests: this._st.interestsLookup?.map(x => {
        return {
          id: x.id,
          name: x.name,
          category: x.category,
          isSelected: false
        } as InterestInterface;
      }),
      reservations: [],
      offers: [],
      mappingFields: {} as MappingFieldsInterface,
      communication: [],
      type: null,
      score: null,
      count: 0,
      dataHubVersion: 0,
      version: 0,
      loyaltiesList: [],
      modifiedBySystem: null,
      createdBySystem: null,
      primaryLoyaltyId: null,
      mergeSource: null,
      isMerge: false,
    } as AccountInterface;
  }

  public AccountFix = (m: AccountInterface | null): AccountInterface | null => {
    if (!m) { return m; }
    m.contact.additional = m.contact.additional ? m.contact.additional : {} as AdditionalInterface;
    m.contact.additional.emails = m.contact.additional.emails ? m.contact.additional.emails : [];
    m.contact.additional.phones = m.contact.additional.phones ? m.contact.additional.phones : [];

    m.advertisingPermissions = this.FixAdvertisingPermissions(m.advertisingPermissions);
    m.products = this.FixProducts(m.products);
    m.address = m.address ? m.address : {} as AddressInterface;
    m.birthDate = this._u.getDateOnlyFromString(m.birthDate);
    m.loyalties = m.loyalties ? m.loyalties : [];
    return m;
  }

  public FixAdvertisingPermissions = (input: { [key: string]: 'Yes' | 'No' | 'Not specified' } | null): { [key: string]: 'Yes' | 'No' | 'Not specified' } => {
    const tmp = new Object() as { [key: string]: 'Yes' | 'No' | 'Not specified' };
    if (input) {
      this._st.advertisingPermissions.forEach(x => {
        if (!Object.keys(input).includes(x.key)) {
          tmp[x.key] = 'Not specified';
        } else {
          tmp[x.key] = input[x.key];
        }
      });
      return tmp;
    } else {
      this._st.advertisingPermissions.forEach(x => {
        tmp[x.key] = 'Not specified';
      });
      return tmp;
    }
  }

  public FixProducts = (input: { [key: string]: 'Yes' | 'No' | 'Not specified' } | null): { [key: string]: 'Yes' | 'No' | 'Not specified' } => {
    const tmp = new Object() as { [key: string]: 'Yes' | 'No' | 'Not specified' };
    if (input) {
      this._st.products.forEach(x => {
        if (!Object.keys(input).includes(x.key)) {
          tmp[x.key] = 'Not specified';
        } else {
          tmp[x.key] = input[x.key];
        }
      });
      return tmp;
    } else {
      this._st.products.forEach(x => {
        tmp[x.key] = 'Not specified';
      });
      return tmp;
    }
  }

  public UpdateCommunicationAccountData = () => {
    if (this._st.editModel() && this._st.contactId() && this._st.editModel().id) {
      this._srvc.updateCommunicationAccount({ contactId: this._st.contactId(), accountId: this._st.editModel().id } as CommunicationUpdateAccountRequestInterface)
        .pipe(
          take(1),
          tap(result => {
            if (!result) {
              this._ms.danger({ title: 'Error', message: 'Warning: Account data were not updated', autoCloseMs: 3000 });
            }

            this.synchronizeActivatedContact();
          }),
          catchError((err: HttpErrorResponse) => {
            this._ms.danger({ title: 'Error', message: err?.error?.message, autoCloseMs: 3000 });
            this.synchronizeActivatedContact();
            return of(null);
          })
        )
        .subscribe();
    }
    else {
      this.synchronizeActivatedContact();
    }
  }

  public synchronizeActivatedContact = () => {
    if (this._st.editModel()) {
      this._setCommunicationSummary();
    }
  }
  public mapAccountToEditAccount = (a: AccountInterface): AccountEditInterface => {
    a.contact.phone = a.contact.phone ?? "";
    a.contact.email = a.contact.email ?? "";
    a.contact.viber = a.contact.viber ?? "";
    a.contact.messenger = a.contact.messenger ?? "";
    return {
      id: a.id ?? '',
      isActive: a.isActive,
      isDeleted: a.isDeleted,
      createdByUser: a.createdByUser ?? '',
      createdDate: a.createdDate ?? '',
      modifiedByUser: a.modifiedByUser ?? '',
      modifiedDate: a.modifiedDate ?? '',
      firstName: a.firstName ?? '',
      lastName: a.lastName ?? '',
      salutation: a.salutation ?? '',
      gender: a.gender ?? '',
      language: a.language ?? '',
      birthDate: a.birthDate,
      age: a.age,
      oib: a.oib ?? '',
      familyStatuses: a.familyStatuses,
      address: a.address,
      contact: a.contact,
      additionalInformation: a.additionalInformation,
      loyalties: a.loyalties,
      advertisingPermissions: a.advertisingPermissions,
      products: a.products,
      interests: a.interests,
      reservations: a.reservations,
      offers: a.offers,
      mappingFields: a.mappingFields,
      communication: a.communication,
      type: a.type,
      score: a.score,
      count: a.count,
      dataHubVersion: a.dataHubVersion,
      version: a.version,
      loyaltiesList: a.loyaltiesList,
      modifiedBySystem: a.modifiedBySystem,
      createdBySystem: a.createdBySystem,
      primaryLoyaltyId: a.primaryLoyaltyId,
      mergeSource: a.mergeSource,
      isMerge: a.isMerge
    } as AccountEditInterface
  }

  public mapAccountToPhones = (a: AccountInterface): { id: number; data: string; }[] => {
    return a.contact?.additional?.phones?.map((e, i) => {
      return { id: i, data: e };
    }) ?? [];
  }
  public mapPhonesToAccount = (a: AccountInterface, phones: { id: number; data: string; }[]): AccountInterface => {
    a.contact.additional.phones = [];
    phones.forEach(x => {
      a.contact.additional.phones.push(x.data);
    });
    return a;
  }
  public mapAccountToEmails = (a: AccountInterface): { id: number; data: string; }[] => {
    return a.contact?.additional?.emails?.map((e, i) => {
      return { id: i, data: e };
    }) ?? [];
  }
  public mapEmailsToAccount = (a: AccountInterface, emails: { id: number; data: string; }[]): AccountInterface => {
    a.contact.additional.emails = [];
    emails.forEach(x => {
      a.contact.additional.emails.push(x.data);
    });
    return a;
  }
  public mapEditAccountToAccount = (a: AccountEditInterface): AccountInterface => {
    return {
      id: a.id || null,
      isActive: a.isActive,
      isDeleted: a.isDeleted,
      createdByUser: a.createdByUser || null,
      createdDate: a.createdDate || null,
      modifiedByUser: a.modifiedByUser || null,
      modifiedDate: a.modifiedDate || null,
      firstName: a.firstName || null,
      lastName: a.lastName || null,
      salutation: a.salutation || null,
      gender: a.gender || null,
      language: a.language || null,
      birthDate: a.birthDate,
      age: a.age,
      oib: a.oib || null,
      familyStatuses: a.familyStatuses,
      address: a.address,
      contact: a.contact,
      additionalInformation: a.additionalInformation,
      loyalties: a.loyalties,
      advertisingPermissions: a.advertisingPermissions,
      products: a.products,
      interests: a.interests,
      reservations: a.reservations,
      offers: a.offers,
      mappingFields: a.mappingFields,
      communication: a.communication,
      type: a.type,
      score: a.score,
      count: a.count,
      dataHubVersion: a.dataHubVersion,
      version: a.version,
      loyaltiesList: a.loyaltiesList,
      modifiedBySystem: a.modifiedBySystem,
      createdBySystem: a.createdBySystem,
      primaryLoyaltyId: a.primaryLoyaltyId,
      mergeSource: a.mergeSource,
      isMerge: a.isMerge
    } as AccountInterface;
  }
  private _setCommunicationSummary = () => {
    const extraData =
      {
        accountId: this._st.editModel().id,
        email: this._st.editModel()?.contact && this._st.editModel()?.contact.email ? this._st.editModel()?.contact.email : '',
        phone: this._st.editModel()?.contact && this._st.editModel()?.contact.phone ? this._st.editModel()?.contact.phone : '',
        localTimeOffset: null,
        filterThreads: null
      } as CommunicationRequestInterface;

    const paramsModel = {
      first: 0,
      rows: 5,
      sortField: undefined,
      sortOrder: 1,
      multiSortMeta: [{ field: 'stateId', order: 1 }, { field: 'createDate', order: -1 }],
      filters: {},
      globalFilter: null,
      extraData: extraData
    } as ValTableState;

    this._srvc.getCommunicationPage(paramsModel)
      .pipe(
        take(1),
        tap(result => {
          if (result && result.data && this._st.editModel()) {
            this._st.editModel.update(x => ({ ...x, communication: result.data }));
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this._ms.danger({ title: 'Error', message: err?.error?.message, autoCloseMs: 3000 });
          return of(null);
        })
      )
      .subscribe();
  }
}
